import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminAPI } from '../services/api';

interface AdminAuthSession {
  id: number;
  admin_id: number;
  admin_username: string;
  admin_role: string;
  login_at: string | null;
  logout_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
  duration_seconds: number;
  is_online: boolean;
}

const AdminAuthMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AdminAuthSession[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const role = localStorage.getItem('admin_role') || 'user';

      if (!token) {
        navigate('/admin/login');
        return;
      }
      if (role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }

      const response = await adminAPI.getAuthSessions({ limit: 100 });
      setSessions(response.data || []);
      setLastUpdate(new Date());
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'خطا در دریافت لاگین کاربران ادمین');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSessions();
    pollingRef.current = setInterval(() => {
      void fetchSessions();
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds: number) => {
    const total = Math.max(0, Number(seconds) || 0);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h > 0) return `${h} ساعت و ${m} دقیقه`;
    return `${m} دقیقه`;
  };

  const formatServerTime = (value: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('fa-IR', {
      timeZone: 'Asia/Tehran',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری مانیتوریگ ادمین..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">مانیتوریگ ادمین</h1>
          <p className="mt-2 text-sm text-gray-600">
            آخرین به‌روزرسانی: {lastUpdate.toLocaleTimeString('fa-IR')}
          </p>
        </div>
        <button
          onClick={() => void fetchSessions()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          به‌روزرسانی
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">گزارش ورود کاربران ادمین</h2>
        </div>
        <div className="p-6">
          {sessions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">لاگ ورودی ثبت نشده است</p>
          ) : (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto" style={{ direction: 'rtl' }}>
              {sessions.map((session) => (
                <div key={session.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-900">{session.admin_username}</p>
                    <span className={`px-2 py-1 text-xs rounded-full ${session.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                      {session.is_online ? 'آنلاین' : 'خارج شده'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    ورود: {formatServerTime(session.login_at)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    مدت حضور: {formatDuration(session.duration_seconds)}
                  </p>
                  {session.logout_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      خروج: {formatServerTime(session.logout_at)}
                    </p>
                  )}
                  {session.ip_address && (
                    <p className="text-[11px] text-gray-400 mt-1">IP: {session.ip_address}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAuthMonitoring;
